// BUG-1888: page-wide guard for unsaved in-panel edits. While armed, any press or
// keyboard focus on the page outside the edit form is swallowed and reported, so
// the panel can ask "Discard unsaved changes?" before anything on the page acts.

'use client';

import { useEffect, useRef } from 'react';

/**
 * Marks the page area the guard watches — the dashboard shell (rail, page, docked
 * panel). Portaled popovers and dialogs render outside it, so a menu the edit
 * form opens, and the discard dialog itself, are never swallowed.
 */
export const PAGE_GUARD_ZONE_ATTR = 'data-page-guard-zone';

/**
 * Opts one control inside the zone out of the guard because it already routes
 * through the same discard prompt itself (the panel's header ✕).
 */
export const PAGE_GUARD_ALLOW_ATTR = 'data-page-guard-allow';

/**
 * The events that START a press. Swallowing them keeps a drag from starting
 * (`@hello-pangea/dnd` reads mousedown) and an input from taking focus
 * (mousedown's default action). Wheel and scroll are deliberately absent, so
 * mouse-wheel, trackpad and scrollbar scrolling stay live (AC 6). A touch on
 * the page beside the panel is a press too, so on a touch screen a scroll
 * gesture there asks first — the docked host is a desktop-width tier.
 */
const PRESS_START_EVENTS = ['pointerdown', 'mousedown', 'touchstart', 'contextmenu'] as const;

/**
 * The events that END a press, plus click. Swallowed only when the press began
 * on guarded page chrome, so a text selection or slider drag that starts in the
 * form and is released over the page is left alone. A click with no press
 * behind it (keyboard activation) is still guarded.
 */
const PRESS_END_EVENTS = ['pointerup', 'mouseup', 'touchend', 'click', 'dblclick', 'auxclick'] as const;

export interface UsePageInteractionGuardOptions {
  /** Guard only while the surface holds unsaved edits. */
  armed: boolean;
  /** The edit form. Presses and focus inside it are the operator editing, never guarded. */
  safeRootRef: React.RefObject<HTMLElement | null>;
  /** Called on every swallowed press or focus — the caller opens its discard prompt. */
  onIntercept: () => void;
}

/**
 * A press on a scroll container's own scrollbar targets the container itself,
 * at an offset past its client box. Scrolling is never guarded (AC 6).
 */
function isScrollbarPress(event: Event, el: Element): boolean {
  if (!(event instanceof MouseEvent) || !(el instanceof HTMLElement)) return false;
  const scrolls = el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;
  return scrolls && el.clientWidth > 0 && (event.offsetX > el.clientWidth || event.offsetY > el.clientHeight);
}

/**
 * Whether an event target is page chrome the guard must swallow: inside the
 * shell zone, outside the edit form, not an opted-out control, not a scrollbar.
 */
function isGuardedTarget(event: Event, safeRoot: HTMLElement | null): boolean {
  const target = event.target;
  if (!(target instanceof Element)) return false;
  if (!target.closest(`[${PAGE_GUARD_ZONE_ATTR}]`)) return false;
  if (safeRoot?.contains(target)) return false;
  if (target.closest(`[${PAGE_GUARD_ALLOW_ATTR}]`)) return false;
  return !isScrollbarPress(event, target);
}

/**
 * Guards the whole page against acting while a panel holds unsaved edits
 * (BUG-1888). Rather than wiring each page control to the panel — brittle, and
 * every new control would need remembering — it listens at the window in the
 * capture phase, ahead of React and every library handler, and swallows any
 * press or focus that lands on the page outside the edit form.
 *
 * @param options.armed - true while there are unsaved edits; listeners exist only then.
 * @param options.safeRootRef - the edit form; never guarded.
 * @param options.onIntercept - opens the caller's discard prompt.
 * @sideEffect Adds capture-phase window/document listeners while armed. A
 *   swallowed focus is handed back to the last focused element in the form.
 */
export function usePageInteractionGuard({ armed, safeRootRef, onIntercept }: UsePageInteractionGuardOptions): void {
  // Latest callback without re-binding the listeners on every render.
  const onInterceptRef = useRef(onIntercept);
  useEffect(() => {
    onInterceptRef.current = onIntercept;
  }, [onIntercept]);

  useEffect(() => {
    if (!armed) return;
    let lastSafeFocus: HTMLElement | null =
      document.activeElement instanceof HTMLElement && safeRootRef.current?.contains(document.activeElement)
        ? document.activeElement
        : null;

    // Whether the current press began somewhere the guard lets through (the
    // form, an opted-out control, outside the zone). Its release is then free.
    // The guard can arm mid-gesture (a picker that commits on press), so the
    // first gesture is credited to the form when focus is already in it.
    let pressStartedFree = lastSafeFocus !== null;

    const swallow = (event: Event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      onInterceptRef.current();
    };

    const onPressStart = (event: Event) => {
      pressStartedFree = !isGuardedTarget(event, safeRootRef.current);
      if (!pressStartedFree) swallow(event);
    };

    const onPressEnd = (event: Event) => {
      const free = pressStartedFree;
      if (event.type === 'click') pressStartedFree = false;
      if (!free && isGuardedTarget(event, safeRootRef.current)) swallow(event);
    };

    // Hand focus back to the form, so the prompt returns it there on Cancel
    // (AC 12). If the last focused field is gone, fall back to the form's first
    // focusable; failing that, drop focus — leaving it on page chrome would
    // re-open the prompt every time Cancel restored it.
    const returnFocusToForm = (from: HTMLElement) => {
      const fallback = safeRootRef.current?.querySelector<HTMLElement>(
        'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      const next = lastSafeFocus?.isConnected ? lastSafeFocus : fallback;
      if (next) next.focus();
      else from.blur();
    };

    const onFocusIn = (event: FocusEvent) => {
      const target = event.target;
      if (target instanceof HTMLElement && safeRootRef.current?.contains(target)) {
        lastSafeFocus = target;
        return;
      }
      if (!(target instanceof HTMLElement) || !isGuardedTarget(event, safeRootRef.current)) return;
      returnFocusToForm(target);
      onInterceptRef.current();
    };

    const options = { capture: true, passive: false } as const;
    for (const type of PRESS_START_EVENTS) window.addEventListener(type, onPressStart, options);
    for (const type of PRESS_END_EVENTS) window.addEventListener(type, onPressEnd, options);
    document.addEventListener('focusin', onFocusIn, true);
    return () => {
      for (const type of PRESS_START_EVENTS) window.removeEventListener(type, onPressStart, options);
      for (const type of PRESS_END_EVENTS) window.removeEventListener(type, onPressEnd, options);
      document.removeEventListener('focusin', onFocusIn, true);
    };
  }, [armed, safeRootRef]);
}
